import { buttonStyle } from 'components/buttons/buttonStyle';
import NumberInput from 'components/forms/inputs/rhf-inputs/NumberInput.tsx';
import { DataTable } from 'components/tables/data-table';
import { Button } from 'components/ui/button';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import type { DataTableColumnDef } from 'components/tables/data-table';
import { useUpdateImportationDateMutation } from 'store/api/tenants.ts';
import { FeatureFlag } from 'types/features';
import { ImportationDateUpdateDto, TenantConfigurationDto } from 'types/tenants.ts';
import { t } from 'utils/getTranslations.ts';
import { useHasPermissions } from 'utils/permissionUtil';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';

interface Props {
    tenantConfiguration: TenantConfigurationDto;
}

const ImportationDateControl = ({ tenantConfiguration }: Props): JSX.Element => {
    const [updateImportationDate] = useUpdateImportationDateMutation();
    const isConfigurationUpdateEnabled = useIsFeatureEnabled(FeatureFlag.IMPORTATION_DATE_CONFIGURATION);
    const hasUpdatePermission = useHasPermissions(['UPDATE.BACK_OFFICE_SETTINGS']);

    const { handleSubmit, control } = useForm<ImportationDateUpdateDto>({
        defaultValues: {
            invoiceAccountingDayOfMonth: tenantConfiguration.invoiceAccountingDayOfMonth,
        },
    });

    const columns = useMemo<DataTableColumnDef<TenantConfigurationDto>[]>(
        () => [
            {
                header: t('configuration.importation-date'),
                id: 'importation-date',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 300,
                },
                cell: () => (
                    <div className="flex gap-4">
                        <NumberInput
                            control={control}
                            name="invoiceAccountingDayOfMonth"
                            inputProps={{ min: 1, max: 31 }}
                            required
                            disabled={!hasUpdatePermission}
                        />
                        <Button
                            {...buttonStyle({ variant: 'contained', color: 'secondary' }, 'h-9')}
                            onClick={() => {
                                void handleSubmit((data) => updateImportationDate(data))();
                            }}
                            disabled={!isConfigurationUpdateEnabled || !hasUpdatePermission}
                        >
                            <FormattedMessage id="save" />
                        </Button>
                    </div>
                ),
            },
            {
                header: '',
                id: 'spacer',
                enableSorting: false,
                meta: {
                    widthWeight: 3,
                },
                cell: () => null,
            },
        ],
        [control, handleSubmit, hasUpdatePermission, isConfigurationUpdateEnabled, updateImportationDate]
    );

    const rows = useMemo(() => [tenantConfiguration], [tenantConfiguration]);

    return (
        <>
            <div className="flex justify-between">
                <h3 className="text-h3 text-balance">
                    <FormattedMessage id="configuration.importation-date-control" />
                </h3>
            </div>
            <DataTable<TenantConfigurationDto>
                columns={columns}
                data={rows}
                columnConfigId="configuration-importation-date"
            />
        </>
    );
};

export default ImportationDateControl;
